import React, { useState } from "react";
import { Link } from "react-router-dom";

const TOS_KEY = "payrollnoot_tos_accepted_v1";

export const hasTosAccepted = () => localStorage.getItem(TOS_KEY) === "true";
export const acceptTos = () => localStorage.setItem(TOS_KEY, "true");

const TosModal = ({ onAccept }) => {
    const [scrolled, setScrolled] = useState(false);

    const handleScroll = (e) => {
        const el = e.target;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) setScrolled(true);
    };

    const handleAccept = () => {
        acceptTos();
        onAccept();
    };

    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.6)", display: "flex",
            alignItems: "center", justifyContent: "center", padding: 16,
        }}>
            <div style={{
                background: "#fff", borderRadius: 12, width: "100%", maxWidth: 680,
                maxHeight: "90vh", display: "flex", flexDirection: "column",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}>
                {/* Header */}
                <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #e5e7eb" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ fontSize: 28 }}>📋</span>
                        <div>
                            <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: "#1a3a5c" }}>Terms of Service</h2>
                            <p style={{ fontSize: 12, color: "#666", margin: 0 }}>Please read and accept before continuing</p>
                        </div>
                    </div>
                </div>

                {/* Scrollable ToS Body */}
                <div onScroll={handleScroll} style={{
                    flex: 1, overflowY: "auto", padding: "20px 24px",
                    fontSize: 13, lineHeight: 1.7, color: "#374151",
                }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: "#1a3a5c", marginBottom: 8 }}>PayrollNoot Payroll Software — Terms of Service</p>
                    <p style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>Last updated: January 1, 2025</p>

                    <p>By accessing and using PayrollNoot ("the Software"), you ("the Company" or "Administrator") agree to be bound by these Terms of Service. Please read them carefully.</p>

                    <p style={{ fontWeight: 700, marginTop: 16, color: "#1a3a5c" }}>1. Service Description</p>
                    <p>PayrollNoot provides payroll calculation, processing, reporting, and compliance tools for businesses. The Software is provided as a management tool and does not constitute legal, tax, accounting, or financial advice.</p>

                    <p style={{ fontWeight: 700, marginTop: 16, color: "#1a3a5c" }}>2. User Responsibilities</p>
                    <p>You are solely responsible for: (a) the accuracy of all data entered into the Software, including employee information, compensation, tax elections, and deduction amounts; (b) verifying all payroll calculations before authorizing payment; (c) timely filing and remittance of all federal, state, and local taxes; (d) compliance with all applicable federal, state, and local employment and payroll laws; and (e) maintaining the confidentiality of your login credentials.</p>

                    <p style={{ fontWeight: 700, marginTop: 16, color: "#1a3a5c" }}>3. Tax Calculations and Compliance Disclaimer</p>
                    <p>The Software provides automated payroll tax calculations based on published IRS and state tax tables. These calculations are provided for informational purposes only. <strong>PayrollNoot does not guarantee the accuracy of tax calculations and is not responsible for penalties, interest, or liabilities arising from incorrect tax filings or payments.</strong> Tax laws change frequently; you are responsible for ensuring your tax bracket data is current and your payroll setup is correct.</p>

                    <p style={{ fontWeight: 700, marginTop: 16, color: "#1a3a5c" }}>4. No Filing or Remittance Services</p>
                    <p>PayrollNoot does not file tax returns or remit tax payments on your behalf. You are responsible for all tax filing deadlines, payment deposits (EFTPS, state portals), and related compliance obligations. The IRS report previews and PDF exports generated by the Software are for your records and are not submitted to any government agency.</p>

                    <p style={{ fontWeight: 700, marginTop: 16, color: "#1a3a5c" }}>5. Data Privacy and Security</p>
                    <p>You acknowledge that you are collecting and processing sensitive employee data (including SSNs, financial information, and personal details) and agree to maintain appropriate security measures. PayrollNoot encrypts data in transit and at rest but cannot guarantee absolute security. You are responsible for complying with all applicable data privacy laws, including but not limited to state data breach notification laws.</p>

                    <p style={{ fontWeight: 700, marginTop: 16, color: "#1a3a5c" }}>6. Limitation of Liability</p>
                    <p>To the maximum extent permitted by law, PayrollNoot and its affiliates, officers, directors, and employees shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to lost profits, tax penalties, interest charges, or regulatory fines, arising from your use of the Software.</p>

                    <p style={{ fontWeight: 700, marginTop: 16, color: "#1a3a5c" }}>7. Modifications</p>
                    <p>We reserve the right to modify these Terms at any time. Continued use of the Software constitutes acceptance of the modified Terms.</p>

                    <p style={{ fontWeight: 700, marginTop: 16, color: "#1a3a5c" }}>8. Governing Law</p>
                    <p>These Terms shall be governed by the laws of the State of Delaware, without regard to conflict of law principles.</p>

                    <p style={{ marginTop: 20, padding: "12px 16px", background: "#fef9c3", borderRadius: 8, border: "1px solid #fbbf24", fontSize: 12, color: "#78350f" }}>
                        <strong>⚠️ Important:</strong> You are acting as the authorized payroll administrator for your company. All payroll actions taken through this system are your legal responsibility. Consult a licensed CPA or payroll professional for tax compliance guidance.
                    </p>

                    {!scrolled && (
                        <p style={{ textAlign: "center", color: "#aaa", fontSize: 12, marginTop: 12 }}>↓ Scroll down to read the full terms before accepting</p>
                    )}
                </div>

                {/* Footer */}
                <div style={{ padding: "16px 24px", borderTop: "1px solid #e5e7eb", background: "#f9fafb", borderRadius: "0 0 12px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <p style={{ fontSize: 11, color: "#888", margin: 0 }}>
                            View full <Link to="/terms" target="_blank" style={{ color: "#1B75F0" }}>Terms of Service</Link> in a new tab.
                        </p>
                        <div style={{ display: "flex", gap: 10 }}>
                            <button
                                onClick={handleAccept}
                                disabled={!scrolled}
                                style={{
                                    background: scrolled ? "#1B75F0" : "#d1d5db",
                                    color: scrolled ? "#fff" : "#9ca3af",
                                    border: "none", borderRadius: 8,
                                    padding: "10px 28px", fontWeight: 700,
                                    fontSize: 14, cursor: scrolled ? "pointer" : "not-allowed",
                                    transition: "background 0.2s",
                                }}
                            >
                                I Agree & Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TosModal;
